import React, { useContext, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { BiPlusCircle, BiPlus, BiShow, BiPencil, BiTrash, BiDownload, BiUpload } from 'react-icons/bi';
import CompanyService from '../../../core/service/CompanyService';
import UtilsService from '../../../core/service/UtilsService';
import './EnterpriseStructure.scss';
import EnterpriseStructureModal from './EnterpriseStructureModal';
import { FaAngleDoubleRight, FaAngleDoubleDown } from 'react-icons/fa';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from "react-toastify";
import ConfirmDialog from '../../../core/utilities/ConfirmPopup';
import useHasPermission from '../../../core/hooks/useHasPermission';
import { LoaderContext } from '../../../App';




const EnterpriseStructure = ({
  t,
  company,
  epNodes,
  onEPStructureRefresh,
  parent
}) => {

  const [nodes, setNodes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [modalDisplay, setModalDisplay] = useState(false);
  const [nodeId, setNodeId] = useState(0);
  const [parentId, setParentId] = useState(0);
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState({});
  const [draggedOver, setDraggedOver] = useState({ nodeId: null, parentId: null });
  const [expandedNodes, setExpandedNodes] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const { hasSettingsUpdatePermission } = useHasPermission();
  const { setLoaderDisplay } = useContext(LoaderContext);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    getCountries();
  }, [])

  useEffect(() => {
    if (epNodes?.length === 0) return;

    setNodes(epNodes);
  }, [epNodes])

  const getEnterpriseStructure = () => {
    CompanyService.getEnterpriseStructure()
      .then(res => {
        if (res.status !== 200) return;

        setNodes(res.data.data);
        onEPStructureRefresh(res.data.data);
      })
      .catch(err => {
      });
  };

  const getCountries = () => {
    UtilsService.getCountryList()
      .then((response) => {
        setCountries([...response.data]);
      })
      .catch((error) => {
      });
  };


  const onNewNode = (parent) => {
    setNodeId(0);
    setParentId(parent?.id ?? 0);
    setModalDisplay(true);
    setSelectedNode(null);
  };

  const onView = (node, parent) => {
    setNodeId(node.id);
    setParentId(parent?.id ?? 0);
    setModalDisplay(true);
    setSelectedNode({ ...node, openedFor: 'VIEW' });
  };

  const onEdit = (node, parent) => {
    setNodeId(node.id);
    setParentId(parent?.id ?? 0);
    setModalDisplay(true);
    setSelectedNode({ ...node, openedFor: 'EDIT' });
  };

  const [deleteModal, setDeleteModal] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const onDeleteClick = (node, parent) => {
    setNodeToDelete({ node, parent });
    setDeleteModal(true);
  };
  const onDeleteConfirm = () => {
    CompanyService.deleteEnterprise(nodeToDelete.node.id)
      .then((response) => {
        if (response.status !== 204 && response.status !== 200) return;

        toast.success("Success", {
          position: toast.POSITION.TOP_RIGHT,
        });
        getEnterpriseStructure();
        // Close dialog and clear selection after successful delete
        setDeleteModal(false);
        setNodeToDelete(null);
      })
      .catch((error) => {
      });
  };

  const onNodeDragStart = (e, node) => {
    setDraggedNode(node);
  };

  const onNodeDragEnd = (e, node) => {
    if (draggedNode.id !== draggedOver.nodeId) {
      const payload = {
        parentId: draggedOver.parentId ?? null,
        prevBusinessUnitId: draggedOver.nodeId
      };
      CompanyService.reorderEnterprise(draggedNode.id, payload)
        .then(res => {
          if (res.status !== 200) return;

          getEnterpriseStructure();
        }).catch(er => { });
    }
    setDraggedOver({});
    setDraggedNode({});
  };

  const onDragOver = (e, node, parent) => {
    setDraggedOver({ nodeId: node.id, parentId: parent?.id ?? null });
  };
  const toggleNode = (node) => {
    setExpandedNodes(prevState => {
      const isExpanded = prevState[node.id];
      const newState = { ...prevState, [node.id]: !isExpanded };

      // Load children if not already loaded and node is expanded
      if (!isExpanded && !node.children) {
        CompanyService.getChildren(node.id).then(res => {
          if (res.status === 200) {
            setNodes(prevNodes => {
              const updatedNodes = [...prevNodes];
              const updateNode = (nodes) => {
                nodes.forEach(n => {
                  if (n.id === node.id) {
                    n.children = res.data.data;
                  } else if (n.children) {
                    updateNode(n.children);
                  }
                });
              };
              updateNode(updatedNodes);
              return updatedNodes;
            });
          }
        });
      }

      return newState;
    });
  };

  const downloadTemplate = () => {
    CompanyService.downloadBusinessUnitTemplate()
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'business-unit-template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentElement.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Template downloaded successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .catch((error) => {
        toast.error("Failed to download template", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    uploadBusinessUnitFile(file);
  };

  const uploadBusinessUnitFile = (file) => {
    setIsImporting(true);
    setLoaderDisplay(true);
    CompanyService.importBusinessUnitData(file)
      .then((response) => {
        if (response.status !== 200 && response.status !== 201) return;

        const summary = response.data?.data;
        const successCount = Number(summary?.successCount ?? 0);
        const errorCount = Number(summary?.errorCount ?? 0);
        const errors = Array.isArray(summary?.errors) ? summary.errors : [];

        if (errorCount > 0 || errors.length > 0) {
          const summaryText = t("Import.result.partial.label", { imported: successCount });
          const detail = errors.length > 0 ? ` ${errors.join(" ")}` : "";
          toast.warning(`${summaryText}${detail}`, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: false,
          });
        } else {
          toast.success(t("Import.result.success.label", { imported: successCount }), {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        getEnterpriseStructure();
      })
      .catch((error) => {
        toast.error("Failed to import business unit data", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        setIsImporting(false);
        setLoaderDisplay(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });
  };



  const renderNodes = (nodes, parent, depth = 0) => {
    let level = 1;
    return (
      <ul>
        {
          nodes?.length > 0 && nodes?.sort((a, b) => a.sortOrder - b.sortOrder)?.map((node, index) => {
            const hasChildren = node.children && node.children.length > 0;

            return (
              <li key={`{nodeKey-${node.id}-${index}}`}>
                {
                  draggedOver.nodeId === node.id && <NodeDropSpacer />
                }
                <div
                  className='node-holder'
                  onDragOver={(e) => onDragOver(e, node, parent)}
                >
                  <div className='tree_box'

                    draggable={true}
                    onDragStart={(e) => onNodeDragStart(e, node)}
                    onDragEnd={(e) => onNodeDragEnd(e, node)}>
                    {/* <div className='tree_box_head'></div> */}
                    <div className='tree_box_body'>
                      <h6 className='fw-600'>{node.name}</h6>
                      <div className='d-flex justify-content-between align-items-end'>
                        <div>
                          <div className='Unittype_box'>
                            {node.unitType?.name}
                          </div>
                          <div className='text-muted'>{node.code}</div>
                        </div>

                        <div className='tree_box_sidebar'>
                          <div className='tree_box_sidebar_in'>
                            <ul>
                              {!parent && (
                                <li  hidden={!hasSettingsUpdatePermission()} onClick={() => onNewNode(node)}>
                                  <BiPlus className='m-0' />
                                </li>
                              )}
                              <li hidden={!hasSettingsUpdatePermission()} onClick={() => onEdit(node, parent)}>
                                <BiPencil className='m-0' />
                              </li>
                              <li onClick={() => onView(node, parent)}>
                                <BiShow className='m-0' />
                              </li>
                              <li hidden={!hasSettingsUpdatePermission()} onClick={() => onDeleteClick(node, parent)}>
                                <BiTrash className='m-0' />
                              </li>
                            </ul>
                          </div>
                        </div>


                      </div>
                    </div>

                  </div>
                </div>
                <div className='toggle-btn'>
                  {hasChildren && (
                    <div className='toggle-btn_inner'>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-${node.id}`}>{t("ShowChildEntitiesToolTip.label")}</Tooltip>}
                      >
                        <div className='toggle-button ' onClick={(e) => { e.stopPropagation(); toggleNode(node); }}>
                          {expandedNodes[node.id] ? <FaAngleDoubleRight />
                            : <FaAngleDoubleDown />}

                        </div>
                      </OverlayTrigger>
                    </div>
                  )}
                </div>


                {expandedNodes[node.id] && (node?.children === null || node?.children?.length > 0) && renderNodes(node.children, node, level + depth)}
              </li>
            )
          })
        }
        {
          depth < 2 && !parent &&
          <li>
            <div className='new-node border-primary' hidden={!hasSettingsUpdatePermission()}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`tooltip-parent`}>{t("CreateEnterprise.label")}</Tooltip>}
              >
                <a className="text-primary cursor" onClick={() => onNewNode(parent)}>
                  <BiPlusCircle className="me-2"></BiPlusCircle>
                </a>
              </OverlayTrigger>
            </div>
          </li>
        }
      </ul>
    );
  };

  return (
    <>
      <div className='tree'>
        <ul>
          <li>
            <div className='Main_company tree_box'>
              <div className='tree_box_head' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <b>{company?.legalName}</b>
                  {
                    company?.regNumber &&
                    <div>ID : {company?.regNumber}</div>
                  }
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-download`}>{t("DownloadTemplate.label")}</Tooltip>}
                  >
                    <button
                      onClick={downloadTemplate}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        padding: '5px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px'
                      }}
                      title="Download Template"
                    >
                      <BiDownload size={24} />
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-upload`}>{t("UploadData.label")}</Tooltip>}
                  >
                    <button
                      onClick={triggerFileInput}
                      disabled={isImporting}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#fff',
                        cursor: isImporting ? 'not-allowed' : 'pointer',
                        padding: '5px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        opacity: isImporting ? 0.6 : 1
                      }}
                      title="Upload Data"
                    >
                      <BiUpload size={24} />
                    </button>
                  </OverlayTrigger>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
              <div className='tree_box_body'>
                {
                  company?.address &&
                  <div className='mb-1'>
                    <div className='text-muted'>{t('Address.label')}</div>
                    {
                      (company?.address?.building || company?.address?.street) &&
                      `${company?.address?.building ?? ''} ${company?.address?.building ? ', ' : ''}${company?.address?.street ?? ''}`
                    }
                    {
                      (company?.address?.city || company?.address?.state) &&
                      `${company?.address?.city ?? ''} ${company?.address?.city ? ', ' : ''}${company?.address?.state?.name ?? ''}`
                    }
                    {company?.address?.country && <span>, {company?.address?.country?.name}</span>}
                    {company?.address?.zipCode && <span> - {company?.address?.zipCode}</span>}
                  </div>
                }
                {
                  company?.primaryContact &&
                  <div className='mb-1'>
                    <div className='text-muted'>{t('ContactPerson.label')}</div>
                    <div>{company?.primaryContact.name}</div>
                    <div>{company?.primaryContact.contactNumber?.number}</div>
                  </div>
                }
                <div className='add_branchBox' hidden={!hasSettingsUpdatePermission()} onClick={() => onNewNode(parent)}>
                  <BiPlus className='m-0' />
                </div>
              </div>
            </div>
            {renderNodes(nodes, null)}
          </li>
        </ul>
      </div>
      {
        modalDisplay &&
        <EnterpriseStructureModal
          showModal={modalDisplay}
          id={nodeId}
          parentId={parentId}
          modalData={selectedNode}
          countries={countries}
          parent={nodes}
          onSaveCallBack={() => {
            setNodeId(0);
            setParentId(0);
            setSelectedNode(null);
            setModalDisplay(false);
            getEnterpriseStructure();
          }}
          onCloseCallback={() => { setModalDisplay(false); }}>
        </EnterpriseStructureModal>
      }
      {deleteModal && (
        <ConfirmDialog
          open={deleteModal}
          setOpen={(val) => setDeleteModal(val)}
          onConfirm={onDeleteConfirm}
        >
          <div className='text-center'>{t("ConfirmDelete.label")}</div>
        </ConfirmDialog>
      )}
    </>
  );
}

export default withTranslation()(EnterpriseStructure);

const NodeDropSpacer = () => {
  return (
    <div className="pt-3 pb-3"
      style={{ backgroundColor: 'lightcyan' }}>
    </div>
  );
};